/**
 * @fileoverview Centralized configuration for internal searchers (BookmarkSearcher, HistorySearcher, SavedSearcher).
 * @module searchers/IntSearcherConfigs
 */

import { SEARCH_TYPES, MAPAPP_DEFAULTS, MAP_LAYER_ZINDICES, ADVANCED_SEARCH_FIELDS} from '../constants.js';



/**
 * Configuration definitions for all internal searcher types.
 * These configs are merged with the base config when creating searchers.
 * @constant {Object}
 */
export const INTERNAL_SEARCHER_CONFIGS = {
    bookmarks: {
        addNewToTop: true,
        searcherName: SEARCH_TYPES.BOOKMARKS,
        relStorageKey: SEARCH_TYPES.BOOKMARKS,
        allowsResultTypePoi: true,
        allowsResultTypeSearch: false,
        enableCornerDelete: true,
        enableActionBarEdit: true,
        enableActionBarRead: true,
        supportsImportLeadsFromPdf: true,
        emptyMessage: 'No saved bookmarks',
        filterFields: ADVANCED_SEARCH_FIELDS,
        timeStampField: 'timeStampB',
        clearAllConfirmMessage: 'Are you sure you want to delete all saved bookmarks?',
        listerOptions: {
            contentsLabel: 'saved bookmarks',
            showClearAllButton: false,
            supportsIndent: false,
        },
        zIndexLayer: MAP_LAYER_ZINDICES.BOOKMARKS,
    },




    history: {
        addNewToTop: true,
        searcherName: SEARCH_TYPES.HISTORY,
        relStorageKey: SEARCH_TYPES.HISTORY,
        //
        maxItems: MAPAPP_DEFAULTS.RESULT_LIMIT_HISTORY_BEFORE_AUTO_TRUNCATE,
        //
        allowsResultTypePoi: true,
        allowsResultTypeSearch: true,
        enableCornerDelete: true,
        enableActionBarDelete: false,
        enableActionBarSave: true,
        enableRunSearchButton: true,
        enableActionBarRead: true,
        emptyMessage: 'No items in history.',
        // the Bookmarks fields (lead and pcat included, so a lead number filters History too) plus the
        // search text of the history's search entries
        filterFields: [...ADVANCED_SEARCH_FIELDS, 'searchQuery'],
        timeStampField: 'timeStampH',
        clearAllConfirmMessage: 'Are you sure you want to delete all search history items?',
        listerOptions: {
            contentsLabel: 'history of recent searches and activity',

            supportsIndent: false,
        },
    },


    savedsearch: {
        addNewToTop: true,
        searcherName: SEARCH_TYPES.SAVEDSEARCH,
        relStorageKey: SEARCH_TYPES.SAVEDSEARCH,
        allowsResultTypePoi: false,
        allowsResultTypeSearch: true,
        enableCornerDelete: true,
        enableActionBarEdit: true,
        enableActionBarRead: true,
        enableRunSearchButton: true,
        emptyMessage: 'No saved searches',
        filterFields: ['searchType', 'searchQuery', 'userLabel', 'userNotes'],
        timeStampField: 'timeStampB',
        clearAllConfirmMessage: 'Are you sure you want to delete all saved searches?',
        listerOptions: {
            contentsLabel: 'saved searches',
            showClearAllButton: false,
            supportsIndent: false,
        },
    }
};






/**
 * Creates a merged configuration for an internal searcher with optional overrides.
 * @param {string} type (see SEARCH_TYPES)
 * @param {Object} [overrides={}] - Optional configuration overrides
 * @returns {Object} Merged configuration object
 */
export function createIntSearcherConfig(type, overrides = {}) {
    const baseConfig = INTERNAL_SEARCHER_CONFIGS[type];
    if (!baseConfig) {
        throw new Error(`Unknown internal searcher type: ${type}`);
    }

    return {
        ...baseConfig,
        ...overrides,
        listerOptions: {
            ...baseConfig.listerOptions,
            ...(overrides.listerOptions || {})
        }
    };
}
